import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, CheckCircle2, Save, XCircle } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../ui/button.tsx';
import { Label } from '../ui/label.tsx';
import type { Specification } from '../Spec/SpecDataList.tsx';
import type { ReleaseTask } from './ReleaseTasksPage.tsx';

declare const process: {
  env: {
    REACT_APP_API_URL?: string;
  };
};

interface ExperimentDto {
  id: string;
  batchNumber: string;
  createdAt: string;
}

interface ExperimentResultItemDto {
  id: string;
  experimentId: string;
  analysis?: string | null;
  testValue: string;
  unit: string;
}

interface BatchOption {
  batchNumber: string;
  latestCreatedAt: string;
}

interface MatchOption {
  id: string;
  label: string;
  analysis: string;
  testValueRaw: string;
  unit: string;
  score: number;
}

interface Step2Row {
  rowKey: string;
  parameter: string;
  testMethod: string;
  specMinRaw: number | string | null;
  specMaxRaw: number | string | null;
  specUnit: string;
  matchOptions: MatchOption[];
  selectedMatchId: string | null;
}

interface ComparisonResult {
  batchNumber: string;
  parameter: string;
  testMethod: string;
  uom: string;
  analysis: string;
  specMin: number | null;
  specMax: number | null;
  specMinDisplay: string;
  specMaxDisplay: string;
  specUnit: string | null;
  testValue: number | null;
  testValueRaw: string;
  units: string;
  passed: boolean;
  reason?: string;
}

interface CreateReleaseTaskProps {
  specifications: Specification[];
  onBack: () => void;
  onSave: (task: ReleaseTask, results: ComparisonResult[]) => void;
}

const normalizeText = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[_\s\-/()]+/g, '');

const levenshteinDistance = (left: string, right: string): number => {
  const a = left;
  const b = right;
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  const rows = a.length + 1;
  const cols = b.length + 1;
  const dp: number[][] = Array.from({ length: rows }, () => Array(cols).fill(0));

  for (let i = 0; i < rows; i += 1) dp[i][0] = i;
  for (let j = 0; j < cols; j += 1) dp[0][j] = j;

  for (let i = 1; i < rows; i += 1) {
    for (let j = 1; j < cols; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost
      );
    }
  }

  return dp[a.length][b.length];
};

const tokenizeForSimilarity = (value: string): string[] =>
  value
    .toLowerCase()
    .split(/[^a-z0-9\u4e00-\u9fa5]+/i)
    .map((token) => token.trim())
    .filter(Boolean);

const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim().replace(/,/g, '');
    if (!trimmed) return null;
    const parsed = Number(trimmed.replace(/%/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const scoreMatch = (left: string, right: string): number => {
  const normalizedLeft = normalizeText(left);
  const normalizedRight = normalizeText(right);
  if (!normalizedLeft || !normalizedRight) return 0;

  if (normalizedLeft === normalizedRight) return 100;

  const leftTokens = tokenizeForSimilarity(left);
  const rightTokens = tokenizeForSimilarity(right);

  const leftSet = new Set(leftTokens);
  const rightSet = new Set(rightTokens);
  const intersection = Array.from(leftSet).filter((token) => rightSet.has(token)).length;
  const union = new Set([...leftSet, ...rightSet]).size;
  const jaccard = union === 0 ? 0 : intersection / union;

  const distance = levenshteinDistance(normalizedLeft, normalizedRight);
  const maxLen = Math.max(normalizedLeft.length, normalizedRight.length);
  const levenshteinSimilarity = maxLen === 0 ? 0 : 1 - distance / maxLen;

  const containsBoost =
    normalizedLeft.includes(normalizedRight) || normalizedRight.includes(normalizedLeft) ? 1 : 0;

  const weighted =
    levenshteinSimilarity * 0.55 +
    jaccard * 0.35 +
    containsBoost * 0.1;

  return Math.round(Math.max(0, Math.min(100, weighted * 100)));
};

const stripBracketContent = (value: string): string => {
  const text = String(value || '').trim();
  if (!text) return '';
  const stripped = text
    .replace(/\[[^\]]*\]/g, ' ')
    .replace(/\([^)]*\)/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return stripped || text;
};

const toDisplayText = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

const normalizeComparableText = (value: string): string =>
  value.toLowerCase().trim().replace(/\s+/g, ' ');

const toShortText = (value: string, maxLength = 10): string => {
  const text = String(value || '');
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
};

const evaluateSingleToken = (
  token: string,
  specMin: number | null,
  specMax: number | null,
  specMinText: string,
  specMaxText: string
): { passed: boolean; reason: string } => {
  const raw = token.trim();
  if (!raw) return { passed: true, reason: '' };

  const normalizedToken = normalizeComparableText(raw);

  const comparatorMatch = raw.match(/^([<>]=?|≤|≥)\s*(-?\d+(?:\.\d+)?)$/);
  if (comparatorMatch) {
    const op = comparatorMatch[1];
    const threshold = Number(comparatorMatch[2]);
    if (!Number.isFinite(threshold)) {
      return { passed: false, reason: `${raw} is not a valid number` };
    }

    if (op === '<' || op === '<=' || op === '≤') {
      if (specMin === null && specMax !== null) {
        const passed = op === '<' ? threshold < specMax : threshold <= specMax;
        return {
          passed,
          reason: passed ? '' : `${raw} is not lower than Spec Max (${specMax})`,
        };
      }

      if (specMin === null) {
        return { passed: false, reason: `Spec Min and Spec Max are missing for ${raw}` };
      }

      const passed = op === '<' ? threshold < specMin : threshold <= specMin;
      return {
        passed,
        reason: passed ? '' : `${raw} is not lower than Spec Min (${specMin})`,
      };
    }

    if (specMax === null && specMin !== null) {
      const passed = op === '>' ? threshold > specMin : threshold >= specMin;
      return {
        passed,
        reason: passed ? '' : `${raw} is not higher than Spec Min (${specMin})`,
      };
    }

    if (specMax === null) {
      return { passed: false, reason: `Spec Max and Spec Min are missing for ${raw}` };
    }

    const passed = op === '>' ? threshold > specMax : threshold >= specMax;
    return {
      passed,
      reason: passed ? '' : `${raw} is not higher than Spec Max (${specMax})`,
    };
  }

  const plainNumber = toNumber(raw);
  if (plainNumber !== null) {
    // Business rule: testValue < 0.4 and specMax > 0.4 is considered pass.
    if (plainNumber < 0.4 && specMax !== null && specMax > 0.4) {
      return {
        passed: true,
        reason: '',
      };
    }

    const reasons: string[] = [];
    if (specMin !== null && plainNumber < specMin) {
      reasons.push(`${plainNumber} below Spec Min (${specMin})`);
    }
    if (specMax !== null && plainNumber > specMax) {
      reasons.push(`${plainNumber} above Spec Max (${specMax})`);
    }
    return {
      passed: reasons.length === 0,
      reason: reasons.join('; '),
    };
  }

  const specCandidates = [specMinText, specMaxText]
    .map((value) => normalizeComparableText(value))
    .filter(Boolean);

  if (specCandidates.length === 0) {
    return { passed: false, reason: `Spec text is missing for string value ${raw}` };
  }

  const maxSpecLength = specCandidates.reduce((max, candidate) => Math.max(max, candidate.length), 0);

  // Business rule:
  // 1) If spec string length > 10, test value containing "matching" passes.
  // 2) If spec string length <= 10, only exact match passes.
  if (maxSpecLength > 10) {
    if (normalizedToken.includes('matching')) {
      return { passed: true, reason: '' };
    }

    return {
      passed: false,
      reason: `String value ${raw} must contain 'matching' when spec text length is greater than 10`,
    };
  }

  const passed = specCandidates.includes(normalizedToken);
  return {
    passed,
    reason: passed ? '' : `String value ${raw} does not match Spec Min/Max`,
  };
};

const evaluateTestValueAgainstSpec = (
  testValueRaw: string,
  specMinRaw: number | string | null,
  specMaxRaw: number | string | null
): { passed: boolean; reason: string; numericValue: number | null } => {
  const values = String(testValueRaw || '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);

  if (values.length === 0) {
    return { passed: false, reason: 'TestValue is empty', numericValue: null };
  }

  const specMin = toNumber(specMinRaw);
  const specMax = toNumber(specMaxRaw);
  const specMinText = toDisplayText(specMinRaw);
  const specMaxText = toDisplayText(specMaxRaw);

  const reasons: string[] = [];
  values.forEach((token) => {
    const result = evaluateSingleToken(token, specMin, specMax, specMinText, specMaxText);
    if (!result.passed && result.reason) {
      reasons.push(result.reason);
    }
  });

  const numericValue = values.length === 1 ? toNumber(values[0]) : null;
  return {
    passed: reasons.length === 0,
    reason: reasons.join('; '),
    numericValue,
  };
};

export default function CreateReleaseTask({ specifications, onBack, onSave }: CreateReleaseTaskProps) {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const taskId = searchParams.get('taskId');
  const sourceTaskId = searchParams.get('sourceTaskId');
  const isViewMode = mode === 'view';
  const isRetestMode = mode === 'retest';

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [batchOptions, setBatchOptions] = useState<BatchOption[]>([]);
  const [selectedBatch, setSelectedBatch] = useState('');
  const [step2Rows, setStep2Rows] = useState<Step2Row[]>([]);
  const [comparisonResults, setComparisonResults] = useState<ComparisonResult[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  const [savingTask, setSavingTask] = useState(false);
  const [errorText, setErrorText] = useState('');

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';

  const authHeaders = useCallback((json = false): HeadersInit => {
    const token = localStorage.getItem('token');
    return {
      ...(json ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }, []);

  const buildComparisonRows = useCallback((rows: Step2Row[], batchNumber: string): ComparisonResult[] => {
    const matchedRows = rows.filter((row) => row.selectedMatchId);
    return matchedRows.map((row) => {
      const match = row.matchOptions.find((item) => item.id === row.selectedMatchId) || null;
      const testValueRaw = match?.testValueRaw || '';
      const evaluation = evaluateTestValueAgainstSpec(testValueRaw, row.specMinRaw, row.specMaxRaw);

      return {
        batchNumber,
        parameter: row.parameter,
        testMethod: row.testMethod,
        uom: row.specUnit || '',
        analysis: match?.analysis || '',
        specMin: toNumber(row.specMinRaw),
        specMax: toNumber(row.specMaxRaw),
        specMinDisplay: toDisplayText(row.specMinRaw),
        specMaxDisplay: toDisplayText(row.specMaxRaw),
        specUnit: row.specUnit || null,
        testValue: evaluation.numericValue,
        testValueRaw,
        units: match?.unit || '',
        passed: evaluation.passed,
        reason: evaluation.reason,
      };
    });
  }, []);

  const mapCompareDtosToRows = useCallback((batchNumber: string, compareItems: Array<{
    batchNumber: string;
    parameter: string;
    testMethod: string;
    uom: string;
    analysis: string;
    units: string;
    specMin: string;
    specMax: string;
    testValue: string;
    testResult: string;
  }>): ComparisonResult[] => {
    return compareItems.map((item) => ({
      batchNumber: item.batchNumber || batchNumber,
      parameter: item.parameter || '-',
      testMethod: item.testMethod || '-',
      uom: item.uom || '-',
      analysis: item.analysis || '-',
      specMin: toNumber(item.specMin),
      specMax: toNumber(item.specMax),
      specMinDisplay: String(item.specMin || ''),
      specMaxDisplay: String(item.specMax || ''),
      specUnit: item.uom || null,
      testValue: toNumber(item.testValue),
      testValueRaw: String(item.testValue || ''),
      units: item.units || '',
      passed: String(item.testResult || '').toLowerCase().includes('pass'),
    }));
  }, []);

  const fetchCompareRowsByBatch = useCallback(async (batchNumber: string): Promise<ComparisonResult[]> => {
    const compareRes = await fetch(`${API_URL}/ReleaseTasksCompare/batch/${encodeURIComponent(batchNumber)}`, {
      method: 'GET',
      headers: authHeaders(),
    });
    if (!compareRes.ok) {
      throw new Error(`Load comparison result failed: HTTP ${compareRes.status}`);
    }

    const compareItems = (await compareRes.json()) as Array<{
      batchNumber: string;
      parameter: string;
      testMethod: string;
      uom: string;
      analysis: string;
      units: string;
      specMin: string;
      specMax: string;
      testValue: string;
      testResult: string;
    }>;

    return mapCompareDtosToRows(batchNumber, compareItems);
  }, [API_URL, authHeaders, mapCompareDtosToRows]);

  const saveCompareRowsForBatch = useCallback(async (batchNumber: string, rows: ComparisonResult[]) => {
    const existingRes = await fetch(`${API_URL}/ReleaseTasksCompare/batch/${encodeURIComponent(batchNumber)}`, {
      method: 'GET',
      headers: authHeaders(),
    });
    if (!existingRes.ok) {
      throw new Error(`Load existing compare rows failed: HTTP ${existingRes.status}`);
    }

    const existingRows = (await existingRes.json()) as Array<{ id: number }>;
    if (existingRows.length > 0) {
      const deleteRes = await fetch(`${API_URL}/ReleaseTasksCompare/batchDelete`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify(existingRows.map((item) => item.id)),
      });
      if (!deleteRes.ok) {
        const text = await deleteRes.text();
        throw new Error(text || `Delete existing compare rows failed: HTTP ${deleteRes.status}`);
      }
    }

    const payloadItems = rows.map((item) => ({
      BatchNumber: batchNumber,
      Parameter: item.parameter,
      TestMethod: item.testMethod || '',
      Uom: item.uom || '',
      Analysis: item.analysis || '',
      Units: item.units || '',
      SpecMin: item.specMinDisplay || '',
      SpecMax: item.specMaxDisplay || '',
      TestValue: item.testValueRaw || String(item.testValue ?? ''),
      TestResult: item.passed ? 'passed' : 'failed',
    }));

    const createRes = await fetch(`${API_URL}/ReleaseTasksCompare/batch`, {
      method: 'POST',
      headers: authHeaders(true),
      body: JSON.stringify(payloadItems),
    });
    if (!createRes.ok) {
      const text = await createRes.text();
      throw new Error(text || `Save compare rows failed: HTTP ${createRes.status}`);
    }
  }, [API_URL, authHeaders]);

  const fetchBatchOptions = useCallback(async () => {
    setLoadingData(true);
    setErrorText('');
    try {
      const params = new URLSearchParams();
      params.set('range', '[0,9999]');
      params.set('perPage', '10000');

      const response = await fetch(`${API_URL}/experiments?${params.toString()}`, {
        method: 'GET',
        headers: authHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Load experiments failed: HTTP ${response.status}`);
      }

      const experiments = (await response.json()) as ExperimentDto[];
      const map = new Map<string, string>();
      experiments.forEach((item) => {
        const batch = String(item.batchNumber || '').trim();
        if (!batch) return;
        const createdAt = item.createdAt || '';
        const current = map.get(batch);
        if (!current || new Date(createdAt).getTime() > new Date(current).getTime()) {
          map.set(batch, createdAt);
        }
      });

      const options = Array.from(map.entries())
        .map(([batchNumber, latestCreatedAt]) => ({ batchNumber, latestCreatedAt }))
        .sort((a, b) => new Date(b.latestCreatedAt).getTime() - new Date(a.latestCreatedAt).getTime());

      setBatchOptions(options);
    } catch (error) {
      setErrorText(error instanceof Error ? error.message : 'Load batches failed.');
    } finally {
      setLoadingData(false);
    }
  }, [API_URL, authHeaders]);

  const loadStep2Data = useCallback(async (batchNumber: string) => {
    setLoadingData(true);
    setErrorText('');
    try {
      const specParams = new URLSearchParams();
      specParams.set('range', '[0,999]');
      specParams.set('perPage', '1000');
      specParams.set('filter', JSON.stringify({ batchNumber }));

      const experimentParams = new URLSearchParams();
      experimentParams.set('range', '[0,500]');
      experimentParams.set('perPage', '500');
      experimentParams.set('filter', JSON.stringify({ batchNumber }));

      const [specRes, experimentRes] = await Promise.all([
        fetch(`${API_URL}/specifications?${specParams.toString()}`, { method: 'GET', headers: authHeaders() }),
        fetch(`${API_URL}/experiments?${experimentParams.toString()}`, { method: 'GET', headers: authHeaders() }),
      ]);

      if (!specRes.ok) throw new Error(`Load specifications failed: HTTP ${specRes.status}`);
      if (!experimentRes.ok) throw new Error(`Load experiments failed: HTTP ${experimentRes.status}`);

      const specList = (await specRes.json()) as Specification[];
      const exactSpecs = specList.filter((item) => String(item.batchNumber || '').trim() === batchNumber);
      if (exactSpecs.length === 0) {
        throw new Error('No specification records found for selected batch.');
      }

      const experiments = (await experimentRes.json()) as ExperimentDto[];
      const exactExperiments = experiments.filter((item) => String(item.batchNumber || '').trim() === batchNumber);
      if (exactExperiments.length === 0) {
        throw new Error('No experiment records found for selected batch.');
      }

      const allExperimentItems = (
        await Promise.all(
          exactExperiments.map(async (exp) => {
            const params = new URLSearchParams();
            params.set('range', '[0,2000]');
            params.set('perPage', '2000');
            params.set('filter', JSON.stringify({ experimentId: exp.id }));

            const res = await fetch(`${API_URL}/experimentresultitems?${params.toString()}`, {
              method: 'GET',
              headers: authHeaders(),
            });

            if (!res.ok) return [] as ExperimentResultItemDto[];
            return (await res.json()) as ExperimentResultItemDto[];
          })
        )
      ).flat();

      const candidates: MatchOption[] = allExperimentItems.map((item) => {
        const analysisRaw = String(item.analysis || '').trim();
        const analysis = stripBracketContent(analysisRaw);
        const testValueRaw = String(item.testValue || '').trim();
        const label = `${analysis || '(No Analysis)'} | ${testValueRaw || '-'} ${item.unit || ''}`.trim();
        return {
          id: `${item.experimentId}:${item.id}`,
          label,
          analysis,
          testValueRaw,
          unit: String(item.unit || '').trim(),
          score: 0,
        };
      });

      const rows = exactSpecs.flatMap((spec, specIndex) =>
        (spec.items || []).map((item, itemIndex) => {
          const sortedMatches = candidates
            .map((candidate) => ({
              ...candidate,
              score: scoreMatch(item.parameter || '', candidate.analysis || ''),
            }))
            .sort((a, b) => b.score - a.score || a.analysis.localeCompare(b.analysis));

          return {
            rowKey: `${spec.id}-${specIndex}-${itemIndex}`,
            parameter: item.parameter || '-',
            testMethod: item.testMethod || '-',
            specMinRaw: item.specMin,
            specMaxRaw: item.specMax,
            specUnit: item.uom || '',
            matchOptions: sortedMatches,
            selectedMatchId: null,
          } as Step2Row;
        })
      );

      const analysisKeyOf = (option: MatchOption): string => {
        const key = normalizeComparableText(option.analysis || '');
        return key || `id:${option.id}`;
      };

      // Enforce one analysis value for one specification row globally,
      // and keep only the most similar candidate per row.
      const rowOrder = rows
        .map((row, index) => ({
          index,
          topScore: row.matchOptions[0]?.score ?? -1,
        }))
        .sort((a, b) => b.topScore - a.topScore)
        .map((item) => item.index);

      const usedAnalysisKeys = new Set<string>();
      const assignedByRow = new Map<number, MatchOption | null>();

      rowOrder.forEach((rowIndex) => {
        const row = rows[rowIndex];
        const bestAvailable = row.matchOptions.find((option) => {
          if (option.score <= 0) return false;
          return !usedAnalysisKeys.has(analysisKeyOf(option));
        }) || null;

        assignedByRow.set(rowIndex, bestAvailable);
        if (bestAvailable) {
          usedAnalysisKeys.add(analysisKeyOf(bestAvailable));
        }
      });

      const finalizedRows = rows.map((row, index) => {
        const assigned = assignedByRow.get(index) || null;
        return {
          ...row,
          matchOptions: assigned ? [assigned] : [],
          selectedMatchId: assigned?.id || null,
        };
      });

      finalizedRows.sort((a, b) => {
        const aMatched = a.selectedMatchId ? 1 : 0;
        const bMatched = b.selectedMatchId ? 1 : 0;
        if (aMatched !== bMatched) return bMatched - aMatched;
        return a.parameter.localeCompare(b.parameter);
      });

      setStep2Rows(finalizedRows);
      setComparisonResults([]);
      setStep(2);
    } catch (error) {
      setErrorText(error instanceof Error ? error.message : 'Load step 2 data failed.');
      setStep2Rows([]);
      setComparisonResults([]);
    } finally {
      setLoadingData(false);
    }
  }, [API_URL, authHeaders]);

  useEffect(() => {
    if (isViewMode) {
      return;
    }

    void fetchBatchOptions();
  }, [fetchBatchOptions, isViewMode]);

  useEffect(() => {
    const batchFromQuery = searchParams.get('batchNumber') || '';
    if (batchFromQuery) {
      setSelectedBatch(batchFromQuery);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!isViewMode) return;

    const loadViewData = async () => {
      setLoadingData(true);
      setErrorText('');
      try {
        let batchNumber = searchParams.get('batchNumber') || '';
        if (!batchNumber && taskId) {
          const taskRes = await fetch(`${API_URL}/ReleaseTasks/${encodeURIComponent(taskId)}`, {
            method: 'GET',
            headers: authHeaders(),
          });
          if (taskRes.ok) {
            const task = await taskRes.json();
            batchNumber = String(task?.batchNumber || '');
          }
        }
        if (!batchNumber) {
          throw new Error('Batch number not found in view mode.');
        }

        setSelectedBatch(batchNumber);
        const rows = await fetchCompareRowsByBatch(batchNumber);

        setComparisonResults(rows);
        setStep(3);
      } catch (error) {
        setErrorText(error instanceof Error ? error.message : 'Load view mode data failed.');
      } finally {
        setLoadingData(false);
      }
    };

    void loadViewData();
  }, [API_URL, authHeaders, fetchCompareRowsByBatch, isViewMode, searchParams, taskId]);

  const matchedCount = useMemo(
    () => step2Rows.filter((row) => row.selectedMatchId !== null).length,
    [step2Rows]
  );

  const selectedMatchIds = useMemo(
    () => new Set(step2Rows.map((row) => row.selectedMatchId).filter((id): id is string => !!id)),
    [step2Rows]
  );

  const selectedAnalysisKeys = useMemo(() => {
    const keys = new Set<string>();
    step2Rows.forEach((row) => {
      if (!row.selectedMatchId) return;
      const selected = row.matchOptions.find((option) => option.id === row.selectedMatchId);
      if (!selected?.analysis) return;
      keys.add(normalizeComparableText(selected.analysis));
    });
    return keys;
  }, [step2Rows]);

  const passCount = useMemo(
    () => comparisonResults.filter((item) => item.passed).length,
    [comparisonResults]
  );

  const handleApplyStep2 = async () => {
    if (!selectedBatch.trim()) {
      setErrorText('Batch Number is required.');
      return;
    }

    const calculatedRows = buildComparisonRows(step2Rows, selectedBatch);
    if (calculatedRows.length === 0) {
      setErrorText('No comparison rows to save.');
      return;
    }

    setLoadingData(true);
    setErrorText('');
    try {
      await saveCompareRowsForBatch(selectedBatch, calculatedRows);
      const savedRows = await fetchCompareRowsByBatch(selectedBatch);
      setComparisonResults(savedRows);
      setStep(3);
    } catch (error) {
      setErrorText(error instanceof Error ? error.message : 'Save step 2 matching failed.');
    } finally {
      setLoadingData(false);
    }
  };

  const handleSave = () => {
    const saveTask = async () => {
      if (!selectedBatch.trim()) {
        throw new Error('Batch Number is required.');
      }
      if (comparisonResults.length === 0) {
        throw new Error('No comparison rows to save.');
      }

      const allPassed = comparisonResults.every((item) => item.passed);
      const token = localStorage.getItem('token');

      const payload = {
        BatchNumber: selectedBatch,
        Result: allPassed ? 'passed' : 'failed',
      };

      let taskRes: Response;
      if (isRetestMode && sourceTaskId) {
        taskRes = await fetch(`${API_URL}/ReleaseTasks/${encodeURIComponent(sourceTaskId)}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            BatchNumber: selectedBatch,
            Result: payload.Result,
          }),
        });
      } else {
        taskRes = await fetch(`${API_URL}/ReleaseTasks`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(payload),
        });
      }

      if (!taskRes.ok) {
        const text = await taskRes.text();
        throw new Error(text || `Save release task failed: HTTP ${taskRes.status}`);
      }

      const saved = await taskRes.json().catch(() => null);
      const taskSource = saved?.task ?? saved;

      const newTask: ReleaseTask = {
        id: String(taskSource?.id ?? sourceTaskId ?? Date.now().toString()),
        batchNumber: selectedBatch,
        status: allPassed ? 'passed' : 'failed',
        result: allPassed ? 'passed' : 'failed',
        createdBy: String(localStorage.getItem('username') || '-'),
        createdAt: taskSource?.createdAt || new Date().toISOString(),
        updatedAt: taskSource?.updatedAt || new Date().toISOString(),
        totalItems: comparisonResults.length,
        passedItems: passCount,
      };

      onSave(newTask, comparisonResults);
    };

    setSavingTask(true);
    saveTask()
      .catch((error) => {
        setErrorText(error instanceof Error ? error.message : 'Save failed.');
      })
      .finally(() => setSavingTask(false));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={onBack} className="hover:bg-gray-50">
          <ArrowLeft className="size-4 mr-2" />
          Back
        </Button>
        <div>
          <h2 className="text-2xl">Create Release Task</h2>
          <p className="text-sm text-gray-500">3 Steps: Release batch -&gt; Confirmate Specifications -&gt; Release result</p>
        </div>
      </div>

      {!isViewMode && (
        <div className="flex items-center justify-center gap-4 py-4">
          <div className="flex items-center gap-2">
            <div className={`h-10 w-10 rounded-full flex items-center justify-center ${step === 1 ? 'bg-blue-500 text-white' : 'bg-green-500 text-white'}`}>1</div>
            <span className={step === 1 ? 'text-blue-600 text-sm' : 'text-gray-600 text-sm'}>Release batch</span>
          </div>
          <div className={`h-1 w-14 rounded ${step > 1 ? 'bg-blue-500' : 'bg-gray-300'}`} />
          <div className="flex items-center gap-2">
            <div className={`h-10 w-10 rounded-full flex items-center justify-center ${step === 2 ? 'bg-blue-500 text-white' : step > 2 ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-700'}`}>2</div>
            <span className={step === 2 ? 'text-blue-600 text-sm' : 'text-gray-600 text-sm'}>Confirmate Specifications</span>
          </div>
          <div className={`h-1 w-14 rounded ${step > 2 ? 'bg-blue-500' : 'bg-gray-300'}`} />
          <div className="flex items-center gap-2">
            <div className={`h-10 w-10 rounded-full flex items-center justify-center ${step === 3 ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-700'}`}>3</div>
            <span className={step === 3 ? 'text-blue-600 text-sm' : 'text-gray-600 text-sm'}>Release result</span>
          </div>
        </div>
      )}

      {errorText && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {errorText}
        </div>
      )}

      {!isViewMode && step === 1 && (
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b pb-3">
            <div>
              <h3 className="text-lg">Step 1: Release batch</h3>
              <span className="text-xs text-gray-500">Distinct from Experiment.BatchNumber (latest first)</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                disabled={!selectedBatch || loadingData}
                onClick={() => {
                  const exact = batchOptions.some((item) => item.batchNumber === selectedBatch.trim());
                  if (!exact) {
                    setErrorText('Please select an existing batch number from the dropdown list.');
                    return;
                  }
                  void loadStep2Data(selectedBatch.trim());
                }}
              >
                Next
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="batchSelectInput">Release batch</Label>
            <input
              id="batchSelectInput"
              list="release-batch-options"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={selectedBatch}
              onChange={(e) => setSelectedBatch(e.target.value)}
              placeholder="Type batch number to search, then select from dropdown"
              disabled={loadingData}
              readOnly={isRetestMode && !!searchParams.get('batchNumber')}
            />
            <datalist id="release-batch-options">
              {batchOptions.map((item) => (
                <option key={item.batchNumber} value={item.batchNumber}>
                  {item.batchNumber} ({new Date(item.latestCreatedAt).toLocaleString('zh-CN', { hour12: false })})
                </option>
              ))}
            </datalist>
            <p className="text-xs text-gray-500">Input to filter, then select one batch from the dropdown list.</p>
          </div>

        </div>
      )}

      {!isViewMode && step === 2 && (
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b pb-3">
            <div>
              <h3 className="text-lg">Step 2: Confirmate Specifications</h3>
              <p className="text-sm text-gray-500">batch number:{selectedBatch}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 mr-2">Matched {matchedCount} / {step2Rows.length}</span>
              <Button variant="outline" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button
                type="button"
                disabled={matchedCount === 0}
                onClick={() => { void handleApplyStep2(); }}
              >
                Next
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-2 text-left">Parameter</th>
                  <th className="px-3 py-2 text-left">Test Method</th>
                  <th className="px-3 py-2 text-left">UOM</th>
                  <th className="px-3 py-2 text-left">Analysis</th>
                  <th className="px-3 py-2 text-left">Unit</th>
                </tr>
              </thead>
              <tbody>
                {step2Rows.map((row) => {
                  const selected = row.matchOptions.find((item) => item.id === row.selectedMatchId) || null;
                  const hasMatch = !!row.selectedMatchId;
                  const availableOptions = row.matchOptions.filter((option) => {
                    if (row.selectedMatchId === option.id) return true;
                    if (selectedMatchIds.has(option.id)) return false;

                    const optionAnalysisKey = normalizeComparableText(option.analysis || '');
                    if (optionAnalysisKey && selectedAnalysisKeys.has(optionAnalysisKey)) {
                      return false;
                    }

                    return true;
                  });
                  return (
                    <tr key={row.rowKey} className={hasMatch ? 'bg-green-50 border-b border-gray-100' : 'bg-white border-b border-gray-100'}>
                      <td className="px-3 py-2">{row.parameter}</td>
                      <td className="px-3 py-2">{row.testMethod || '-'}</td>
                      <td className="px-3 py-2">{row.specUnit || '-'}</td>
                      <td className="px-3 py-2 min-w-[360px]">
                        <select
                          className="w-full rounded border border-gray-300 px-2 py-1"
                          value={row.selectedMatchId || ''}
                          onChange={(e) => {
                            const value = e.target.value || null;
                            setStep2Rows((prev) => prev.map((item) => (
                              item.rowKey === row.rowKey
                                ? { ...item, selectedMatchId: value }
                                : item
                            )));
                          }}
                        >
                          <option value="">Unmatched</option>
                          {availableOptions.map((option) => (
                            <option key={option.id} value={option.id}>
                              [{option.score}] {option.analysis || '(No Analysis)'}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2">{selected?.unit || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b pb-3">
            <div>
              <h3 className="text-lg">Step 3: Release result</h3>
              <p className="text-sm text-gray-500">batch number:{selectedBatch || '-'}</p>
            </div>
            <div className="flex items-center gap-2">
              {!isViewMode && (
                <>
                  <Button variant="outline" onClick={() => setStep(2)}>
                    Back
                  </Button>
                  <Button type="button" disabled={savingTask} onClick={handleSave}>
                    <Save className="size-4 mr-2" />
                    {savingTask ? 'Saving...' : 'Save Release Task'}
                  </Button>
                </>
              )}
              {comparisonResults.every((item) => item.passed) ? (
                <>
                  <CheckCircle2 className="size-5 text-green-600" />
                  <span className="text-green-600 text-sm">All Passed</span>
                </>
              ) : (
                <>
                  <XCircle className="size-5 text-red-600" />
                  <span className="text-red-600 text-sm">Has Failed Items</span>
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-center">
              <div className="text-xl">{comparisonResults.length}</div>
              <div className="text-xs text-gray-600">Total</div>
            </div>
            <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-center">
              <div className="text-xl text-green-700">{passCount}</div>
              <div className="text-xs text-green-700">Pass</div>
            </div>
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-center">
              <div className="text-xl text-red-700">{comparisonResults.length - passCount}</div>
              <div className="text-xs text-red-700">Failed</div>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-gray-200">
            <table className="w-full table-fixed text-xs md:text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-2 text-left whitespace-normal break-words">Parameter</th>
                  <th className="px-3 py-2 text-left whitespace-normal break-words">Test Method</th>
                  <th className="px-3 py-2 text-left whitespace-normal break-words">UOM</th>
                  <th className="px-3 py-2 text-left whitespace-normal break-words">Analysis</th>
                  <th className="px-3 py-2 text-left whitespace-normal break-words">Units</th>
                  <th className="px-3 py-2 text-left whitespace-normal break-words">Spec Min</th>
                  <th className="px-3 py-2 text-left whitespace-normal break-words">Spec Max</th>
                  <th className="px-3 py-2 text-left whitespace-normal break-words">TestValue</th>
                  <th className="px-3 py-2 text-left whitespace-normal break-words">TestResult</th>
                </tr>
              </thead>
              <tbody>
                {comparisonResults.map((item, index) => {
                  return (
                    <tr key={`${item.parameter}-${index}`} className={item.passed ? 'bg-green-50 border-b border-gray-100' : 'bg-red-50 border-b border-gray-100'}>
                      <td className="px-3 py-2 align-top whitespace-normal break-words">{item.parameter}</td>
                      <td className="px-3 py-2 align-top whitespace-normal break-words">{item.testMethod || '-'}</td>
                      <td className="px-3 py-2 align-top whitespace-normal break-words">{item.uom || '-'}</td>
                      <td className="px-3 py-2 align-top whitespace-normal break-words">{item.analysis || '-'}</td>
                      <td className="px-3 py-2 align-top whitespace-normal break-words">{item.units || '-'}</td>
                      <td
                        className="px-3 py-2 align-top whitespace-normal break-words"
                        title={item.specMinDisplay || '-'}
                      >
                        {item.specMinDisplay ? toShortText(item.specMinDisplay, 30) : '-'}
                      </td>
                      <td
                        className="px-3 py-2 align-top whitespace-normal break-words"
                        title={item.specMaxDisplay || '-'}
                      >
                        {item.specMaxDisplay ? toShortText(item.specMaxDisplay, 30) : '-'}
                      </td>
                      <td className="px-3 py-2 align-top whitespace-normal break-words">{item.testValueRaw || '-'}</td>
                      <td className="px-3 py-2 align-top whitespace-normal break-words">
                        {item.passed ? (
                          <span className="inline-flex items-center gap-1 text-green-700">
                            <CheckCircle2 className="size-4" />
                            Pass
                          </span>
                        ) : (
                          <div className="text-red-700">
                            <span className="inline-flex items-center gap-1">
                              <XCircle className="size-4" />
                              Failed
                            </span>
                            {item.reason && <div className="text-xs mt-0.5">({item.reason})</div>}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

        </div>
      )}

      {loadingData && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
          <div className="rounded-lg border border-gray-200 bg-white px-6 py-4 text-sm text-gray-700 shadow-lg">
            Loading...
          </div>
        </div>
      )}
    </div>
  );
}